import Navbar from "../home/components/Navbar";
import Footer from "../home/components/Footer";
import ScrollToTop from "../../components/feature/ScrollToTop";

export default function PoliticaPrivacidad() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://vpb-barcelona.com';

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Política de Privacidad - Vaciado de Pisos Barcelona VPB",
    "description": "Política de privacidad de Vaciado de Pisos Barcelona. Información sobre el tratamiento de datos personales, derechos del usuario y protección de datos según RGPD.",
    "url": `${siteUrl}/politica-privacidad`,
    "inLanguage": "es-ES",
    "isPartOf": {
      "@type": "WebSite",
      "name": "Vaciado de Pisos Barcelona VPB",
      "url": siteUrl
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Inicio",
        "item": siteUrl
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Política de Privacidad",
        "item": `${siteUrl}/politica-privacidad`
      }
    ]
  };

  return (
    <>
      <title>Política de Privacidad - Vaciado de Pisos Barcelona VPB</title>
      <meta name="description" content="Política de privacidad de Vaciado de Pisos Barcelona. Información sobre el tratamiento de datos personales, derechos del usuario y protección de datos según RGPD." />
      <meta name="robots" content="noindex, follow" />
      <link rel="canonical" href={`${siteUrl}/politica-privacidad`} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

      <div className="min-h-screen bg-white">
        <Navbar />
        <ScrollToTop />
        
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-8">Política de privacidad</h1>
          
          <div className="prose prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Responsable del tratamiento</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                De conformidad con lo establecido en el Reglamento General de Protección de Datos (RGPD) y la 
                Ley Orgánica 3/2018, de 5 de diciembre, de Protección de Datos Personales y garantía de los 
                derechos digitales, le informamos que:
              </p>
              <ul className="list-none text-gray-600 space-y-2">
                <li><strong>Responsable:</strong> Vaciado de Pisos Barcelona (VPB)</li>
                <li><strong>Domicilio:</strong> Barcelona y área metropolitana</li>
                <li><strong>Email:</strong> info@vaciadopisosbarcelona.com.es</li>
                <li><strong>Teléfono:</strong> 629 501 941</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Finalidad del tratamiento</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Los datos personales que nos proporcione a través de los <a href="/#contacto" className="text-teal-600 hover:text-teal-700 underline cursor-pointer">formularios de contacto</a> o solicitud de 
                presupuesto serán tratados con las siguientes finalidades:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                <li>Gestionar y responder a sus consultas y solicitudes de información</li>
                <li>Elaborar y enviar presupuestos personalizados</li>
                <li>Gestionar la prestación de nuestros <a href="/#servicios" className="text-teal-600 hover:text-teal-700 underline cursor-pointer">servicios de vaciado de pisos</a></li>
                <li>Mantener el contacto con nuestros clientes</li>
                <li>Enviar comunicaciones comerciales sobre nuestros servicios (solo con su consentimiento previo)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Legitimación</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                La base legal para el tratamiento de sus datos personales es:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                <li>El consentimiento del interesado al facilitarnos sus datos a través de los formularios</li>
                <li>La ejecución de un contrato en el que el interesado es parte</li>
                <li>El interés legítimo del responsable</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Conservación de datos</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Los datos personales proporcionados se conservarán mientras se mantenga la relación comercial o 
                durante los años necesarios para cumplir con las obligaciones legales. Una vez finalizada la 
                relación, los datos serán bloqueados durante el plazo de prescripción de las acciones legales 
                que pudieran derivarse y posteriormente eliminados.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Destinatarios de los datos</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Sus datos no serán cedidos a terceros, salvo obligación legal. No obstante, le informamos que 
                podrán tener acceso a sus datos personales aquellos proveedores que nos presten servicios de 
                alojamiento web, mantenimiento informático y gestión de comunicaciones, con los que hemos suscrito 
                los correspondientes contratos de encargo de tratamiento.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Derechos del interesado</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Cualquier persona tiene derecho a obtener confirmación sobre si estamos tratando datos personales 
                que le conciernen. Las personas interesadas tienen derecho a:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                <li>Acceder a sus datos personales</li>
                <li>Solicitar la rectificación de los datos inexactos</li>
                <li>Solicitar su supresión cuando los datos ya no sean necesarios</li>
                <li>Solicitar la limitación de su tratamiento en determinadas circunstancias</li>
                <li>Oponerse al tratamiento de sus datos por motivos relacionados con su situación particular</li>
                <li>Solicitar la portabilidad de los datos en los casos previstos en la normativa</li>
                <li>Otros derechos reconocidos en las normativas aplicables</li>
              </ul>
              <p className="text-gray-600 leading-relaxed mt-4">
                Para ejercer estos derechos, puede dirigirse a nosotros mediante correo electrónico a 
                info@vaciadopisosbarcelona.com.es, adjuntando copia de su DNI o documento equivalente.
              </p>
              <p className="text-gray-600 leading-relaxed mt-4">
                Asimismo, le informamos que tiene derecho a presentar una reclamación ante la Agencia Española 
                de Protección de Datos (www.aepd.es) si considera que el tratamiento de sus datos personales 
                no se ajusta a la normativa vigente.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Seguridad de los datos</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                VPB se compromete a adoptar las medidas técnicas y organizativas necesarias, según el nivel de 
                seguridad adecuado al riesgo de los datos recogidos, de forma que se garantice la seguridad de 
                los datos de carácter personal y se evite la destrucción, pérdida o alteración accidental o 
                ilícita de datos personales transmitidos, conservados o tratados de otra forma, o la comunicación 
                o acceso no autorizados a dichos datos. Consulta también nuestra <a href="/politica-cookies" className="text-teal-600 hover:text-teal-700 underline cursor-pointer">política de cookies</a> y nuestro <a href="/aviso-legal" className="text-teal-600 hover:text-teal-700 underline cursor-pointer">aviso legal</a>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Exactitud y veracidad de los datos</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                El usuario es el único responsable de la veracidad y corrección de los datos que remita, 
                exonerando a VPB de cualquier responsabilidad al respecto. Los usuarios garantizan y responden, 
                en cualquier caso, de la exactitud, vigencia y autenticidad de los datos personales facilitados, 
                y se comprometen a mantenerlos debidamente actualizados.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Modificación de la política de privacidad</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                VPB se reserva el derecho a modificar la presente política para adaptarla a novedades legislativas 
                o jurisprudenciales, así como a prácticas de la industria. En dichos supuestos, anunciará en esta 
                página los cambios introducidos con razonable antelación a su puesta en práctica.
              </p>
            </section>

            <div className="mt-12 p-6 bg-gray-50 rounded-xl">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">¿Tienes dudas sobre privacidad?</h3>
              <p className="text-gray-600 mb-4">
                Si necesitas más información sobre cómo protegemos tus datos o quieres conocer nuestros <a href="/#servicios" className="text-teal-600 hover:text-teal-700 font-semibold underline cursor-pointer">servicios profesionales de vaciado</a>, <a href="/#contacto" className="text-teal-600 hover:text-teal-700 font-semibold underline cursor-pointer">contáctanos sin compromiso</a>.
              </p>
            </div>

            <p className="text-sm text-gray-500 mt-8">
              Última actualización: {new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}